import React, { memo } from 'react';
import {
  Button,
  ModalContent,
  Textarea,
  Text,
  Loader,
  StatusLabel,
  Conditional,
  Space,
} from '@cashfree-intl/coherent';

// Images
import inactiveWebsite from 'images/inactive-website.svg';

// Constants
import { inactiveWebsiteSchema } from '../validation';
import {
  DOMAIN_TYPE,
  INACTIVE_WEBSITE_INITIAL_FORM_DATA,
  TEST_IDS,
  WHITELISTING_STATUS,
} from '../constants';
import { SOURCE_TYPE } from 'utils/constants';

// Components
import FormikComponent from 'components/Formik';

// Styled
import { StyledInactiveWebsiteModal, StyledWebsiteChip } from '../styled';
import {
  StyledCross,
  StyledImage,
  StyledTitle,
} from 'containers/LandingPage/components/ConfirmWebsiteModal/styled';

// Types
import {
  InactiveWebsiteFormikValuesType,
  InactiveWebsiteModalProps,
  InactiveWebsiteValuesType,
} from '../types';

const InactiveWebsiteModal = ({
  modalData,
  isLoading,
  onModalClose,
  addWhitelisting,
  openReviewModal,
}: InactiveWebsiteModalProps) => {
  const onFormSubmit = (values: InactiveWebsiteValuesType, actions) => {
    addWhitelisting({
      reqData: {
        ...modalData,
        domainType: DOMAIN_TYPE.WEBSITE,
        sourceType: SOURCE_TYPE.DASHBOARD,
        reason: values.reason,
        isWebsiteLive: false,
      },
      successCb: res => openReviewModal(res, true),
      errorCb: onModalClose,
    });
    if (actions) actions.setSubmitting(false);
  };
  return (
    <FormikComponent
      initialValues={INACTIVE_WEBSITE_INITIAL_FORM_DATA}
      validationSchema={inactiveWebsiteSchema}
      onSubmit={onFormSubmit}
    >
      {({
        values,
        handleFieldChange,
        handleFieldBlur,
        handleSubmit,
        isValid,
        isValidating,
        isSubmitting,
        errors,
      }: InactiveWebsiteFormikValuesType) => {
        const disableSubmitButton = !isValid || (!isValidating && isSubmitting);
        const hasReasonLengthExceeded = values.reason.length > 600;
        return (
          <StyledInactiveWebsiteModal
            $maxWidth={580}
            open
            data-testid={TEST_IDS.INACTIVE_WEBSITE_MODAL}
          >
            <Loader active={isLoading} page={false} />
            <ModalContent>
              <StyledCross onClick={onModalClose} opacity={0.3} />
              <StyledImage src={inactiveWebsite} alt="inactive-website" />
              <StyledWebsiteChip alignItems="center" className="mb-1">
                <Text variant="b14" color="bodyLight" className="mr-1">
                  {modalData.domainUrl}
                </Text>
                <StatusLabel filled $status={WHITELISTING_STATUS.REJECTED}>
                  Inactive
                </StatusLabel>
              </StyledWebsiteChip>
              <StyledTitle variant="h20" color="bodyLight">
                Your website appears to be inactive/not live! We could be wrong
                though.
              </StyledTitle>
              <Textarea
                rows={4}
                fluid
                label="Please provide us reason for whitelisting."
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                value={values.reason}
                name="reason"
                className="inactive-reason-textarea"
              />
              <Space justifyContent="space-between" className="reason-error">
                <div>
                  <Conditional if={errors.reason}>
                    <Text variant="b12" color="danger">
                      {errors.reason}
                    </Text>
                  </Conditional>
                </div>
                <Text
                  variant="b12"
                  color={hasReasonLengthExceeded ? 'danger' : 'bodyLight'}
                >
                  {values.reason.length || 0}/600
                </Text>
              </Space>
              <Button
                primary
                className="mt-4 px-6"
                disabled={disableSubmitButton}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </ModalContent>
          </StyledInactiveWebsiteModal>
        );
      }}
    </FormikComponent>
  );
};

export default memo(InactiveWebsiteModal);
